
import { Component, Watch, Vue } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { UserPermissions } from "@/store/types";
import { parseTaskText } from "@/filters";
import { stringMixin } from "@/mixins";
import { TaskCategoryType } from "@/models/Tasks/TaskCategoryType";
import { TaskTypeId } from "@/types/Tasks/TaskTypeId";
import { ActionTrigger, Task, TaskCategory } from "@/store/tasks/types";

const Tasks = namespace("tasks");

const { isNullOrWhitespace } = stringMixin.methods;

@Component({
	components: {
		"vue-perfect-scrollbar": VuePerfectScrollbar
	},
	filters: {
		parseTaskText
	}
})
export default class ActionLibrarySimpleList extends Vue {
	@Getter("getPermissions") permissions: UserPermissions;
	@Getter("getCanAddIndividualActionsOverride")
	canAddIndividualActionsOverride: boolean;

	@Tasks.Getter getTaskTriggersByTaskId: (taskId: number) => ActionTrigger[]
	@Tasks.Getter("getTaskLibrary") taskLibrary: any;
	@Tasks.Getter("getTaskCategories") taskCategories: TaskCategory[];
	@Tasks.Getter("getTaskTypes") taskTypes: any;
	@Tasks.Getter("getSelectedActions") selectedActions: any;
	@Tasks.Getter getTaskById: (taskId: number) => Task;
	@Tasks.Getter getTaskTitlesByType: Map<TaskTypeId, string>;
	@Tasks.Getter toggleActionListModal: any;
	@Tasks.Getter hasEventOutcomeTask: boolean;


	@Tasks.Mutation resetTasksSelected: () => void;
	@Tasks.Mutation setCategorySelected: ({ categoryId, selected }) => void;
	@Tasks.Mutation setActionSelected: ({
		categoryId,
		taskId,
		selected
	}) => void;

	@Tasks.Action fetchTaskTriggers: () => Promise<void>;
	@Tasks.Action fetchActionLibrary: any;
	@Tasks.Action fetchTaskCategories: any;

	public filter: string = "";
	public actionsInActionPlansByCategory: any = {};
	public selected: any = {};
	public selectedCategories: string[] = [];

	public async mounted(): Promise<void> {
		await this.fetchTaskTriggers();
	}

	public get canAddIndividualActions() {
		return this.canAddIndividualActionsOverride
			? true
			: this.permissions.canAddIndividualActions;
	}

	public get filterLowerCase() {
		return this.filter.toLowerCase();
	}

	public get taskCategoriesWithTasks() {
		if (this.canAddIndividualActions) {
			return this.taskCategories.filter(
				(category: TaskCategory) =>
					this.filteredTasks(category, category.tasks).length > 0
			);
		} else {
			if (isNullOrWhitespace(this.filter)) {
				return this.taskCategories;
			} else {
				return this.taskCategories.filter(this.categoryInFilter);
			}
		}
	}

	public categoryInFilter(category: any) {
		return (
			this.filter !== null &&
			this.filter.length > 0 &&
			category.tasks.length > 0 &&
			category.title.toLowerCase().indexOf(this.filterLowerCase) > -1
		);
	}

	/**
	 * Filters the list of tasks to:
	 * * Have only tasks that are in the correct category
	 * * No Event Outcome tasks (including sub-actions) if the event already has one
	 * * Be filtered the search text
	 * @param category
	 * @param taskList
	 */
	public filteredTasks(category: TaskCategory, taskList: Task[]) {
		if (this.hasEventOutcomeTask) {

			taskList = taskList.filter(task => {
				//Filter out any Event outcome tasks
				if (task.taskTypeID == TaskTypeId.EventOutcome) {
					return false;
				}

				// Filter out tasks that have a sub task (taskTrigger) of EventOutcome
				const taskTriggers = this.getTaskTriggersByTaskId(task.taskID);

				return !taskTriggers ? true : !taskTriggers.some(taskTrigger => {
					const task = this.getTaskById(taskTrigger.triggerTaskID);

					return task.taskTypeID == TaskTypeId.EventOutcome;
				})
			});
		}

		if (!this.filter){
			return taskList;
		}

		if (this.categoryInFilter(category)) {
			return taskList;
		}

		return taskList.filter((task: any) => {
			switch(task.taskTypeID){
				case TaskTypeId.Dispatch:
					return "dispatch".indexOf(this.filterLowerCase) > -1;
				case TaskTypeId.EventOutcome:
					return "event outcome".indexOf(this.filterLowerCase) > -1;
				default:
					return !this.filter || (task.taskText
						.toLowerCase()
						.indexOf(this.filterLowerCase)) > -1;
			}
		});
	}

	public selectCategory(category: any) {
		let newSelectedState = !category.selected;

		this.setCategorySelected({
			categoryId: category.taskCategoryID,
			selected: newSelectedState
		});

		this.filteredTasks(category, category.tasks).forEach((action: any) => {
			this.setActionSelected({
				categoryId: category.taskCategoryID,
				taskId: action.taskID,
				selected: newSelectedState
			});
		});
	}

	@Watch("toggleActionListModal", { immediate: true })
	public onModalVisibilityChange() {
		this.resetTasksSelected();
	}

	public async created() {
		await this.fetchActionLibrary();
		await this.fetchTaskCategories(TaskCategoryType.Alarm);
	}
}
